'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useWbsStore } from '@/stores/wbsStore';
import { categoriesApi, tasksApi, allocationsApi } from '@/lib/api';
import type { StageArtifact, MemberAllocation } from '@/lib/api';
import type { Task, TaskCreate, TaskStatus } from '@/types';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task?: Task | null;
  insertAfterOrder?: number | null;
  parentTask?: Task | null;  // 하위 작업 추가 시 부모 태스크
}

interface StageTreeItem {
  id: number;
  code: string;
  name: string;
  color: string | null;
  order_index: number;
  children: Array<{
    id: number;
    code: string;
    name: string;
    color: string | null;
    order_index: number;
    parent_id: number;
  }>;
}

// 산출물 아이콘 컴포넌트
function ArtifactIcon({ type }: { type: string }) {
  const cls = "w-3.5 h-3.5 flex-shrink-0";
  switch (type) {
    case 'doc':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
    case 'menu':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>;
    case 'screen':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
    case 'table':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18M8 6v12M16 6v12" /></svg>;
    case 'api':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
    case 'arch':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>;
    case 'code':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>;
    case 'module':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>;
    case 'task':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>;
    case 'scenario':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
    case 'case':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    case 'run':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    case 'plan':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
    case 'env':
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>;
    default:
      return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>;
  }
}

const initialFormData: TaskCreate = {
  stage_name: '',
  parent_task_name: null,
  depth: 2,
  task_name: '',
  manager: '',
  start_date: '',
  end_date: '',
  status: '시작전',
  progress: 0,
  progress_manual: false,
  memo: '',
  order_index: 0,
};

export default function TaskModal({ isOpen, onClose, task, insertAfterOrder, parentTask }: TaskModalProps) {
  const router = useRouter();
  const { createTask, updateTask, refresh } = useWbsStore();
  const [formData, setFormData] = useState<TaskCreate>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // 카테고리 데이터
  const [stageTree, setStageTree] = useState<StageTreeItem[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(true);

  // 산출물 현황
  const [stageArtifacts, setStageArtifacts] = useState<StageArtifact[]>([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactsTotalProgress, setArtifactsTotalProgress] = useState(0);

  // 투입 인원 (담당자 셀렉트용)
  const [memberOptions, setMemberOptions] = useState<{ name: string; department: string; role?: string }[]>([]);

  const isEditMode = !!task;

  // 카테고리 데이터 로드
  useEffect(() => {
    if (!isOpen) return;

    const loadCategories = async () => {
      setCategoryLoading(true);
      try {
        const [tree, statusItems] = await Promise.all([
          categoriesApi.getItemsTree('task_stage'),
          categoriesApi.getItems('task_status'),
        ]);
        setStageTree(tree);
        setStatusOptions(statusItems.map(item => item.code));
      } catch (error) {
        console.error('Failed to load categories:', error);
        // 폴백: 비워두지 않음
        setStageTree([]);
        setStatusOptions(['시작전', '진행중', '컨펌중', '완료']);
      } finally {
        setCategoryLoading(false);
      }
    };

    loadCategories();
  }, [isOpen]);

  // 투입 인원 로드
  useEffect(() => {
    if (!isOpen) return;

    const loadMembers = async () => {
      try {
        const allocs = await allocationsApi.getAll({ status: '투입중' });
        const members = allocs
          .filter((a: MemberAllocation) => a.member_name)
          .map((a: MemberAllocation) => ({
            name: a.member_name!,
            department: a.member_department || '',
            role: a.project_role || '',
          }));
        // 중복 제거 (같은 이름)
        const unique = members.filter((m, i, arr) => arr.findIndex(x => x.name === m.name) === i);
        setMemberOptions(unique);
      } catch {
        setMemberOptions([]);
      }
    };

    loadMembers();
  }, [isOpen]);

  // 산출물 현황 로드 (편집 모드에서 stage_name이 있을 때)
  useEffect(() => {
    if (!isOpen || !isEditMode) {
      setStageArtifacts([]);
      return;
    }

    const stageName = task?.stage_name;
    if (!stageName) return;

    const loadArtifacts = async () => {
      setArtifactsLoading(true);
      try {
        const result = await tasksApi.getStageArtifacts(stageName);
        setStageArtifacts(result.artifacts);
        setArtifactsTotalProgress(result.total_progress);
      } catch {
        setStageArtifacts([]);
      } finally {
        setArtifactsLoading(false);
      }
    };

    loadArtifacts();
  }, [isOpen, isEditMode, task?.stage_name]);

  // Initialize form when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        stage_name: task.stage_name,
        parent_task_name: task.parent_task_name,
        depth: task.depth,
        task_name: task.task_name,
        manager: task.manager || '',
        start_date: task.start_date || '',
        end_date: task.end_date || '',
        status: task.status,
        progress: task.progress,
        progress_manual: task.progress_manual || false,
        memo: task.memo || '',
        order_index: task.order_index,
      });
    } else if (parentTask) {
      // 하위 작업 추가 모드: 부모 태스크의 stage와 task_name을 미리 설정
      setFormData({
        ...initialFormData,
        stage_name: parentTask.stage_name,
        parent_task_name: parentTask.task_name,
        depth: 3,
        order_index: parentTask.order_index + 0.5,
      });
    } else {
      setFormData({
        ...initialFormData,
        order_index: insertAfterOrder ? insertAfterOrder + 0.5 : 0,
      });
    }
    setSaveStatus('idle');
  }, [task, parentTask, insertAfterOrder, isOpen]);

  // Auto-save with debounce
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    setSaveStatus('saving');

    const timeout = setTimeout(() => {
      handleAutoSave();
    }, 1500);

    setAutoSaveTimeout(timeout);
  }, [autoSaveTimeout]);

  const handleAutoSave = async () => {
    if (!validateForm(true)) {
      setSaveStatus('idle');
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode && task) {
        await updateTask(task.id, formData);
      }
      setSaveStatus('saved');
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const validateForm = (silent = false): boolean => {
    if (!formData.stage_name) {
      if (!silent) alert('단계를 선택해주세요.');
      return false;
    }
    if (!formData.task_name) {
      if (!silent) alert('작업명을 입력해주세요.');
      return false;
    }
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      if (!silent) alert('종료일은 시작일 이후여야 합니다.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEditMode && task) {
        await updateTask(task.id, formData);
      } else {
        await createTask(formData);
      }
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof TaskCreate, value: TaskCreate[keyof TaskCreate]) => {
    // 공정율을 수동으로 변경하면 progress_manual=true 설정
    if (field === 'progress') {
      setFormData(prev => ({ ...prev, progress: value as number, progress_manual: true }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (isEditMode) {
      triggerAutoSave();
    }
  };

  const handleStageChange = (stageName: string) => {
    setFormData(prev => ({
      ...prev,
      stage_name: stageName,
      parent_task_name: null,
      depth: 2,
    }));

    if (isEditMode) triggerAutoSave();
  };

  const handleDepth2Change = (taskName: string) => {
    if (taskName) {
      setFormData(prev => ({
        ...prev,
        parent_task_name: taskName,
        depth: 3,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        parent_task_name: null,
        depth: 2,
      }));
    }

    if (isEditMode) triggerAutoSave();
  };

  if (!isOpen) return null;

  // 선택된 1뎁스의 2뎁스 옵션 (카테고리 트리에서 조회)
  const selectedStage = stageTree.find(s => s.name === formData.stage_name);
  const depth2Options = selectedStage?.children?.map(c => c.name) || [];

  // 1뎁스 옵션: 카테고리 트리에서 가져옴
  const stageNames = stageTree.map(s => s.name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {isEditMode ? '작업 수정' : '작업 추가'}
          </h2>
          <div className="flex items-center gap-3">
            {isEditMode && (
              <span className={`text-sm ${
                saveStatus === 'saving' ? 'text-blue-500' :
                saveStatus === 'saved' ? 'text-green-500' :
                saveStatus === 'error' ? 'text-red-500' :
                'text-gray-400'
              }`}>
                {saveStatus === 'saving' && '저장 중...'}
                {saveStatus === 'saved' && '저장됨'}
                {saveStatus === 'error' && '저장 실패'}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {categoryLoading ? (
            <div className="text-center py-4 text-gray-500">카테고리 로딩 중...</div>
          ) : (
            <>
              {/* Stage (1뎁스) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  단계 (1뎁스) <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.stage_name}
                  onChange={(e) => handleStageChange(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">단계 선택</option>
                  {stageNames.map((stage) => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>

              {/* Parent Task (2뎁스) - 카테고리에 등록된 하위 항목 */}
              {formData.stage_name && depth2Options.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    상위 작업 (2뎁스)
                  </label>
                  <select
                    value={formData.parent_task_name || ''}
                    onChange={(e) => handleDepth2Change(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">없음 (2뎁스 작업)</option>
                    {depth2Options.map((taskName) => (
                      <option key={taskName} value={taskName}>{taskName}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    선택하면 3뎁스 작업으로 등록됩니다.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              작업명 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.task_name}
              onChange={(e) => handleChange('task_name', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="작업명을 입력하세요"
              required
            />
          </div>

          {/* Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              담당자
              {memberOptions.length > 0 && (
                <span className="ml-1 text-xs font-normal text-gray-400">
                  (투입인원 {memberOptions.length}명)
                </span>
              )}
            </label>
            {memberOptions.length > 0 ? (
              <select
                value={formData.manager || ''}
                onChange={(e) => handleChange('manager', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">담당자 선택</option>
                {memberOptions.map((member) => (
                  <option key={member.name} value={member.name}>
                    {member.name}{member.department ? ` (${member.department})` : ''}{member.role ? ` · ${member.role}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={formData.manager || ''}
                onChange={(e) => handleChange('manager', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="담당자명"
              />
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                시작일
              </label>
              <input
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                종료일
              </label>
              <input
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              진행상태
            </label>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((status) => (
                <label
                  key={status}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition ${
                    formData.status === status
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={status}
                    checked={formData.status === status}
                    onChange={(e) => handleChange('status', e.target.value as TaskStatus)}
                    className="sr-only"
                  />
                  <span className="text-sm">{status}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                공정율: {formData.progress}%
              </label>
              <div className="flex items-center gap-2">
                {formData.progress_manual ? (
                  <>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">수동</span>
                    {isEditMode && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, progress_manual: false }));
                          if (isEditMode) triggerAutoSave();
                        }}
                        className="text-xs text-blue-500 hover:text-blue-700 underline"
                      >
                        자동으로 전환
                      </button>
                    )}
                  </>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">자동</span>
                )}
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={formData.progress}
              onChange={(e) => handleChange('progress', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Memo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              메모
            </label>
            <textarea
              value={formData.memo || ''}
              onChange={(e) => handleChange('memo', e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="메모를 입력하세요"
            />
          </div>

          {/* 산출물 현황 (편집 모드에서만) */}
          {isEditMode && stageArtifacts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                산출물 현황
                <span className="ml-2 text-xs font-normal text-gray-400">
                  {artifactsTotalProgress}% 완료
                </span>
              </label>
              {artifactsLoading ? (
                <div className="text-sm text-gray-400">로딩 중...</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {stageArtifacts.map((artifact) => (
                    <button
                      key={artifact.key}
                      type="button"
                      onClick={() => {
                        onClose();
                        router.push(artifact.link);
                      }}
                      className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:shadow-md cursor-pointer group"
                      style={{
                        backgroundColor: artifact.progress === 100 ? '#f0fdf4' : artifact.total === 0 ? '#f9fafb' : '#eff6ff',
                        borderColor: artifact.progress === 100 ? '#86efac' : artifact.total === 0 ? '#e5e7eb' : '#93c5fd',
                        color: artifact.progress === 100 ? '#166534' : artifact.total === 0 ? '#6b7280' : '#1e40af',
                      }}
                      title={`${artifact.label}: ${artifact.done}/${artifact.total} 완료${artifact.wip > 0 ? `, ${artifact.wip} 진행중` : ''}${artifact.draft > 0 ? `, ${artifact.draft} 작성중` : ''}`}
                    >
                      {/* 알림 뱃지 */}
                      {artifact.has_alert && (
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                        </span>
                      )}
                      {/* 아이콘 */}
                      <ArtifactIcon type={artifact.icon} />
                      {/* 라벨 */}
                      <span>{artifact.label}</span>
                      {/* 카운트 */}
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold"
                        style={{
                          backgroundColor: artifact.progress === 100 ? '#dcfce7' : artifact.total === 0 ? '#f3f4f6' : '#dbeafe',
                        }}
                      >
                        {artifact.done}/{artifact.total}
                      </span>
                      {/* 링크 화살표 */}
                      <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
              {/* 진행률 바 */}
              {stageArtifacts.length > 0 && (
                <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${artifactsTotalProgress}%`,
                      backgroundColor: artifactsTotalProgress === 100 ? '#22c55e' : artifactsTotalProgress > 50 ? '#3b82f6' : '#f97316',
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isSaving ? '저장 중...' : isEditMode ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
